import { useNavigate } from 'react-router-dom';
import { Button } from '@components/primitives/Button';
import { FeatureCard } from '@components/ui/cards/FeatureCard';
import {
  useCreateEncounterFromAppointmentMutation,
  useCreateEncounterMutation,
} from '@features/encounters';
import { useRequireScheduledAppointmentForNoteCreation, useTemplatesList } from '@features/user';
import { useEventTracking } from '@services/statsig';

import { useUpcomingAppointments } from './hooks/useUpcomingAppointments';
import { AppointmentsList } from './AppointmentsList';

/**
 * Container/Composer component for Upcoming Appointments
 * Orchestrates data fetching, navigation, and event tracking
 * All business logic lives here, sub-components are pure UI
 */
export const UpcomingAppointmentsCard = () => {
  const navigate = useNavigate();
  const { dashboard } = useEventTracking();
  const templatesList = useTemplatesList();
  const requiresScheduledAppointmentForNoteCreation =
    useRequireScheduledAppointmentForNoteCreation();
  const { appointments, loading } = useUpcomingAppointments();

  // RTK Query mutations
  const [createEncounterFromAppointment] = useCreateEncounterFromAppointmentMutation();
  const [createEncounter] = useCreateEncounterMutation();

  // Handle appointment click - navigate or create encounter
  const handleAppointmentClick = async (appointment: {
    id: string;
    originalAppointment?: {
      encounter?: {
        encounter_id: string;
      };
    };
  }) => {
    dashboard.actionClicked({
      action_type: 'appointment_clicked',
      source: 'dashboard_v2_appointments_card',
    });

    // If encounter already exists, navigate to it
    if (appointment.originalAppointment?.encounter?.encounter_id) {
      navigate(`/encounters/${appointment.originalAppointment.encounter.encounter_id}`);
      return;
    }

    // Create encounter from appointment
    try {
      const newEncounter = await createEncounterFromAppointment({
        appointmentId: appointment.id,
      }).unwrap();
      if (newEncounter?.encounter_id) {
        navigate(`/encounters/${newEncounter.encounter_id}`);
      }
    } catch (error) {
      console.error('Error creating encounter:', error);
    }
  };

  // Handle schedule appointment click
  const handleScheduleAppointment = async () => {
    dashboard.actionClicked({
      action_type: 'schedule_appointment',
      source: 'dashboard_v2_appointments_empty',
    });

    if (requiresScheduledAppointmentForNoteCreation) {
      navigate('/schedule');
      return;
    }

    // Preserve the legacy empty-state behavior for orgs that still allow ad-hoc notes.
    if (templatesList?.[0]) {
      try {
        const newEncounter = await createEncounter({
          createEncounterRequest: {
            encounter_type: templatesList[0].display_name,
            template_id: templatesList[0].template_id,
          },
        }).unwrap();
        if (newEncounter?.encounter_id) {
          navigate(`/encounters/${newEncounter.encounter_id}`);
        }
      } catch (error) {
        console.error('Error creating encounter:', error);
      }
    } else {
      navigate('/schedule');
    }
  };

  // Handle view schedule button click
  const handleViewSchedule = () => {
    dashboard.actionClicked({
      action_type: 'view_schedule',
      source: 'dashboard_v2_appointments_card',
    });
    navigate('/schedule');
  };

  // Handle new appointment button click
  const handleNewClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click if card is clickable
    dashboard.actionClicked({
      action_type: 'new_appointment',
      source: 'dashboard_v2_appointments_card',
    });
    navigate('/schedule');
  };

  return (
    <FeatureCard
      title="Next Appointments"
      badge={
        <Button
          size="small"
          variant="outlined"
          onClick={handleNewClick}
          sx={{
            minWidth: 'auto',
            px: 2,
            py: 0.5,
          }}
        >
          New
        </Button>
      }
      fullHeight
      contentSx={{
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        maxHeight: 400,
      }}
    >
      <AppointmentsList
        appointments={appointments}
        onAppointmentClick={handleAppointmentClick}
        onSchedule={handleScheduleAppointment}
        onViewSchedule={handleViewSchedule}
        loading={loading}
      />
    </FeatureCard>
  );
};
